import { InvalidValueException } from "@shared/utils";
import { DateType } from "@shared/contexts";
import DateService from "./DateService";
import { t_warningTexts } from "@i18n/usualTexts/t_warningTexts";
import { TFunction } from "i18next";

export interface User {
  username: string;
  password: string;
  name: string;
}

class AccountService {
  t: TFunction<"translation", undefined>;
  constructor(t: TFunction<"translation", undefined>) {
    this.t = t;
  }

  checkConfirmPassword(
    confirmPassword: string,
    password: string,
    field: string
  ) {
    if (confirmPassword === password) return confirmPassword;

    throw new InvalidValueException([
      field,
      this.t(t_warningTexts.signup.confirmPassword),
    ]);
  }

  checkDate(birthday: DateType, field: string, valueMonths: Array<string>) {
    const { day, month, year } = birthday;
    
    if (day || month || year) {
      const dateService = new DateService(
        day,
        month,
        year,
        field,
        valueMonths,
        this.t
      );

      //Validate Year
      dateService.validateYear();

      //Validate Month
      dateService.validateMonth();

      //Validate Day
      dateService.validateDay();

      //Validate Age
      dateService.validateAge();
    } else {
      throw new InvalidValueException([
        field,
        this.t(t_warningTexts.signup.date),
      ]);
    }
  }

  checkName(name: string, field: string, id: string) {
    const nomePattern = /^[A-Za-zÀ-ÖØ-öø-ÿ'\s-]+$/;

    if (nomePattern.test(name)) return name;

    throw new InvalidValueException([
      field,
      this.t(t_warningTexts.signup.name, { id: id }),
    ]);
  }

  checkPassword(password: string, field: string) {
    const error = this.validatePassword(password);
    if (!error) return password;

    throw new InvalidValueException([field, error]);
  }

  checkSelect(
    value: string,
    field: string,
    id: string,
    values: Array<string>
  ) {
    const trimmedValue = value.trim();
    const trimmedValues = values.map(v => v.trim());
    if (trimmedValues.includes(trimmedValue)) return trimmedValue;

    throw new InvalidValueException([
      field,
      this.t(t_warningTexts.signup.select, { id: id }),
    ]);
  }

  checkTelephone(telephone: string, field: string) {
    const phonePattern = /^\d{11}$/;

    if (!phonePattern.test(telephone))
      throw new InvalidValueException([field, "Insira um telefone válido."]);
  }

  checkUsername(username: string, field: string) {
    const emailPattern = /^[a-zA-Z0-9._+\-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;

    if (!emailPattern.test(username))
      throw new InvalidValueException([
        field,
        this.t(t_warningTexts.signup.username),
      ]);
  }

  checkCode(code: string, field: string) {
    const codePattern = /^\d{6}$/;

    if (codePattern.test(code)) {
      return code;
    }

    throw new InvalidValueException([
      field,
      this.t(t_warningTexts.signup.code),
    ]);
  }

  private static readonly PASSWORD_RULES: ReadonlyArray<{ test: (p: string) => boolean; key: keyof typeof t_warningTexts.signup.password }> = [
    { test: (p) => p.length >= 8, key: 'length' },
    { test: (p) => /\d/.test(p), key: 'number' },
    { test: (p) => /[A-Z]/.test(p), key: 'uppercase' },
    { test: (p) => /[a-z]/.test(p), key: 'lowercase' },
    { test: (p) => /[^a-zA-Z0-9\s]/.test(p), key: 'specialCharacter' },
  ];

  validatePassword(password: string): string {
    const errors = AccountService.PASSWORD_RULES
      .filter(rule => !rule.test(password))
      .map(rule => this.t(t_warningTexts.signup.password[rule.key]));

    if (errors.length > 0) {
      errors.unshift(this.t(t_warningTexts.signup.password.beginning));
      errors[errors.length - 1] = errors[errors.length - 1].replace(",", ".");
    };
    return errors.join(" ");
  }
}

export default AccountService;
